import { type FC, useState } from "react";
import { usePouch, useView } from "use-pouchdb";
import { Link } from "react-router";

import { assertIsItemInfo, isIconDoc } from "../common/schema";
import { assert } from "./utils";
import { useScanner } from "./Scanner";
import { assertIsExistingIconDoc } from "./existing_docs";
import { IconImageFromDoc } from "./images";

export const TemplatePicker: FC<{onSelect: (id: string) => void; onCancel: () => void}> = ({onSelect, onCancel}) => {
  const { rows } = useView("built/not_deleted", { include_docs: true });
  const [search, setSearch] = useState("");
  const templates = rows.filter(row => {
    assertIsItemInfo(row.doc);
    return row.doc.is_template === true &&
      (search === "" || row.doc.name.toLowerCase().includes(search.toLowerCase()));
  });
  return <div style={{border: "1px solid #aaa", padding: "8px", margin: "4px 0"}}>
    <input type="text" placeholder="Search templates…" value={search} onChange={e => setSearch(e.target.value)} autoFocus />
    {templates.length === 0 && <div style={{fontSize: "small", color: "#888"}}>No templates found</div>}
    <div style={{display: "flex", flexDirection: "column", gap: "2px", marginTop: "4px"}}>
      {templates.map(row => {
        assert(row.doc != null);
        assertIsItemInfo(row.doc);
        return <button key={row.doc._id} type="button" onClick={() => onSelect(row.doc!._id)}>
          {row.doc.name || row.doc._id}
        </button>;
      })}
    </div>
    <button type="button" onClick={onCancel} style={{marginTop: "4px"}}>Cancel</button>
  </div>;
};

export const IconPicker: FC<{onSelect: (iconId: string) => void; onCancel: () => void}> = ({onSelect, onCancel}) => {
  const { rows } = useView("built/icons", { include_docs: true });
  const [search, setSearch] = useState("");

  const filtered = rows.filter(row => {
    if (!isIconDoc(row.doc)) return false;
    return search === "" || row.doc.name.toLowerCase().includes(search.toLowerCase());
  });

  return <div style={{border: "1px solid #aaa", padding: "8px", margin: "4px 0"}}>
    <input type="text" placeholder="Search icons…" value={search} onChange={e => setSearch(e.target.value)} autoFocus />
    {filtered.length === 0 && (
      <div style={{fontSize: "small", color: "#888", marginTop: "4px"}}>
        No icons found. Add some in the <Link to="/icons">icon library</Link>.
      </div>
    )}
    <div style={{display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(80px, 1fr))", gap: "8px", marginTop: "8px", maxHeight: "300px", overflowY: "auto"}}>
      {filtered.map(row => {
        assert(row.doc != null);
        assertIsExistingIconDoc(row.doc);
        const iconDoc = row.doc;
        return <button
          key={iconDoc._id}
          type="button"
          onClick={() => onSelect(iconDoc._id)}
          style={{display: "flex", flexDirection: "column", alignItems: "center", gap: "4px", padding: "4px", border: "1px solid #ccc", borderRadius: "4px", cursor: "pointer", background: "white"}}
        >
          <IconImageFromDoc doc={iconDoc} style={{width: "60px", height: "60px"}} />
          <span style={{fontSize: "11px", textAlign: "center", wordBreak: "break-word"}}>{iconDoc.name}</span>
        </button>;
      })}
    </div>
    <button type="button" onClick={onCancel} style={{marginTop: "8px"}}>Cancel</button>
  </div>;
};

export const ItemPicker: FC<{
  label: string;
  onSelect: (id: string) => void;
  onCancel: () => void;
  excludeId?: string;
}> = ({label, onSelect, onCancel, excludeId}) => {
  const { rows } = useView("built/not_deleted", { include_docs: true });
  const db = usePouch();
  const [search, setSearch] = useState("");
  const { startScanner } = useScanner({
    onScanResult: async (result) => {
      if (result == null) return;
      try {
        const found = await db.query("built/item_by_shelcode", { key: result.text, include_docs: true });
        const row = found.rows[0];
        if (row?.id != null) onSelect(row.id);
      } catch { /* ignore */ }
    },
  });
  const filtered = rows.filter(row => {
    if (row.doc == null) return false;
    assertIsItemInfo(row.doc);
    if (row.doc.is_template) return false;
    if (row.doc._id === excludeId) return false;
    const q = search.toLowerCase();
    return q === "" || row.doc.name.toLowerCase().includes(q) || (row.doc.shelcode ?? "").includes(q);
  });
  return <div style={{border: "1px solid #aaa", padding: "8px", margin: "4px 0"}}>
    <div style={{fontWeight: "bold", marginBottom: "4px"}}>{label}</div>
    <div style={{display: "flex", gap: "4px", marginBottom: "4px"}}>
      <input type="text" placeholder="Search by name or shelcode…" value={search}
        onChange={e => setSearch(e.target.value)} autoFocus style={{flexGrow: 1}} />
      <button type="button" onClick={startScanner} title="Scan shelcode">📷</button>
    </div>
    <div style={{maxHeight: "200px", overflowY: "auto"}}>
      {filtered.slice(0, 50).map(row => {
        assert(row.doc != null);
        assertIsItemInfo(row.doc);
        return <div key={row.doc._id} style={{display: "flex", gap: "4px", padding: "2px 0", borderBottom: "1px solid #eee"}}>
          <button type="button" onClick={() => onSelect(row.doc!._id)} style={{flexGrow: 1, textAlign: "left"}}>
            {row.doc.name || <em style={{color: "#aaa"}}>unnamed</em>}
            {row.doc.shelcode && <span style={{marginLeft: "8px", fontFamily: "monospace", fontSize: "small", color: "#888"}}>{row.doc.shelcode}</span>}
          </button>
        </div>;
      })}
      {filtered.length === 0 && <div style={{color: "#888", fontSize: "small"}}>No items found</div>}
    </div>
    <button type="button" onClick={onCancel} style={{marginTop: "4px"}}>Cancel</button>
  </div>;
};
